<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { QSlider } from 'quasar';

import { formatMoney, getLabel, getTranslation, isHtmlEmpty } from '@/utils/data-display-utils';
import { Locales } from '@/store/language.interface';
import useResponsive from '@/utils/responsive-util';
import { Dot } from '@/store/form.Interface';
import { constant } from '@/constant';

import DotVotingChoiceModal from '@/components/modals/DotVotingChoiceModal.vue';

// PROPS
const props = defineProps<{
  dot: Dot;
  dotVotingType: number;
  languageEnabled?: Locales[];
  isSelected: boolean;
  selectedValue?: number | null;
  isDisabled: boolean;
  dotVotingMeasurementType: number;
  defaultCostPerChoice: number;
  numberOfSteps?: number | null;
  remainingBudget?: number;
  hasUserVoted?: boolean;
  hasUserVotedOnChoice?: boolean;
  state: any;
  statistics?: any;
}>();

// EMITS
const emit = defineEmits<{
  'select-vote': [dotId: number, value?: number];
  'update:sliderValue': [{ [dotId: number]: number }];
}>();

// CONST
const { isMobile } = useResponsive();
const State = constant.FormAnswer.FormState;

// REFS
const dotVotingChoiceDialogRef = ref<InstanceType<typeof DotVotingChoiceModal> | null>(null);
const sliderValue = ref(0);
const isActive = ref<boolean>(false);

// COMPUTED
const maxValue = computed(() => props.dot.cost || props.defaultCostPerChoice);
const maxIndex = computed(() => (props.numberOfSteps ? props.numberOfSteps : 1));
const stepSize = computed(() => maxValue.value / maxIndex.value);

const choiceValue = computed(() => {
  if (props.dotVotingType === constant.Form.DotVoting.Type.Fixed) {
    return props.dot.cost || props.defaultCostPerChoice;
  } else {
    return sliderValue.value * stepSize.value;
  }
});

const sliderLabel = computed(() => {
  if (isActive.value && sliderValue.value !== 0) {
    return formatValue(choiceValue.value);
  }
  return getLabel('form.select-value', props.languageEnabled);
});

const labelColor = computed(() => {
  if (isActive.value) {
    return 'secondary';
  } else {
    return 'grey-4';
  }
});

const labelTextColor = computed(() => {
  if (isActive.value) {
    return 'white';
  } else {
    return 'black';
  }
});

const innerMax = computed(() => {
  const budget = Number(props.remainingBudget);
  const step = Number(stepSize.value);
  if (!Number.isFinite(budget) || step <= 0) return sliderValue.value;

  const availableSteps = Math.floor(budget / step);
  const proposedMax = sliderValue.value + availableSteps;
  return Math.min(maxIndex.value, Math.max(sliderValue.value, proposedMax));
});

const gradientFillStyle = computed(() => {
  if (props.state !== State.ResultWithStats) {
    return {};
  }

  const rawPercentage =
    props.dotVotingType === constant.Form.DotVoting.Type.Variable
      ? (props.statistics?.distribution?.share_of_total_allocations ?? 0)
      : (props.statistics?.percentage ?? 0);
  const safePercentage = Math.min(Math.max(rawPercentage, 0), 100);

  return { '--dot-vote-percentage': `${safePercentage}%` };
});

const votesPercentage = computed(() => props.statistics?.percentage ?? 0);

const distributionStats = computed(() => props.statistics?.distribution || null);

const dotTotalAllocation = computed(() => {
  if (props.dotVotingType !== constant.Form.DotVoting.Type.Variable) return null;
  const totalAllocation = Number(props.statistics?.totalAllocation);
  const shareOfTotal = Number(props.statistics?.distribution?.share_of_total_allocations);
  if (!Number.isFinite(totalAllocation) || !Number.isFinite(shareOfTotal)) return null;
  return (totalAllocation * shareOfTotal) / 100;
});

const variableStatsDisplay = computed(() => {
  if (props.dotVotingType !== constant.Form.DotVoting.Type.Variable) return [];
  if (!distributionStats.value) return [];

  const items: string[] = [];

  const formatPoints = (value: number | null) => {
    if (value === null) return null;
    return `${value} ${getLabel('form.dot-voting.points-abbreviation', props.languageEnabled)}`;
  };

  const formatCurrency = (value: number | null) => {
    if (value === null) return null;
    return formatMoney(value, props.languageEnabled);
  };

  const averageRaw = Number(distributionStats.value.average);
  const average = Number.isFinite(averageRaw) ? averageRaw : null;
  const medianRaw = Number(distributionStats.value.median);
  const median = Number.isFinite(medianRaw) ? medianRaw : null;
  const totalAllocation = dotTotalAllocation.value;
  const dotCost = Number(props.statistics?.cost);
  const costValue = Number.isFinite(dotCost) ? dotCost : null;

  if (props.dotVotingMeasurementType === constant.Form.DotVoting.MeasurementType.Points) {
    const averageLabel = average !== null ? formatPoints(average) : null;
    if (averageLabel)
      items.push(`${getLabel('form.dot-voting.results.average', props.languageEnabled)}: ${averageLabel}`);
    const medianLabel = median !== null ? formatPoints(median) : null;
    if (medianLabel) items.push(`${getLabel('form.dot-voting.results.median', props.languageEnabled)}: ${medianLabel}`);
  } else if (props.dotVotingMeasurementType === constant.Form.DotVoting.MeasurementType.Money) {
    const fundedLabel =
      totalAllocation !== null && costValue !== null
        ? getLabel('form.dot-voting.results.funded', props.languageEnabled, {
            named: {
              allocated: formatCurrency((distributionStats.value?.percentage / 100) * costValue),
              cost: formatCurrency(costValue),
            },
          })
        : null;
    if (fundedLabel) items.push(fundedLabel);
  }

  return items;
});

const isFixedInteractiveButton = computed(() => {
  return props.dotVotingType === constant.Form.DotVoting.Type.Fixed && props.state === State.Question;
});

const interactiveTag = computed(() => {
  return isFixedInteractiveButton.value ? 'button' : 'div';
});

const interactiveRole = computed(() => {
  return isFixedInteractiveButton.value ? undefined : 'group';
});

const interactiveAriaPressed = computed(() => {
  return isFixedInteractiveButton.value ? props.isSelected : undefined;
});

const interactiveAriaDisabled = computed(() => {
  return isFixedInteractiveButton.value ? props.isDisabled : undefined;
});

const interactiveClasses = computed(() => {
  return [
    'dot-vote-choice__interactive',
    {
      'dot-vote-choice__interactive--clickable': isFixedInteractiveButton.value,
    },
  ];
});

const isSliderDisabled = computed(() => {
  if (props.dotVotingType !== constant.Form.DotVoting.Type.Variable) {
    return false;
  }

  const budget = Number(props.remainingBudget);
  if (!Number.isFinite(budget)) return false;

  const step = Number(stepSize.value);
  if (step <= 0) return false;

  // Allow interaction when already selected so the user can adjust or clear it.
  if (props.isSelected) return false;

  return budget < step;
});

// FUNCTIONS
const openSeeMoreDialog = (event: Event) => {
  event.stopPropagation();
  dotVotingChoiceDialogRef.value?.openDialog();
};

const selectVote = () => {
  if (props.state !== State.Question) return;
  if (props.dotVotingType === constant.Form.DotVoting.Type.Fixed) {
    emit('select-vote', props.dot.id);
  }
};

const updateSlider = (value: number) => {
  sliderValue.value = value;
  emit('update:sliderValue', { [props.dot.id]: sliderValue.value });
  if (sliderValue.value === 0) {
    emit('select-vote', props.dot.id);
  }
};

const syncSliderValueWithSelection = (value?: number | null) => {
  if (props.dotVotingType !== constant.Form.DotVoting.Type.Variable) return;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    sliderValue.value = 0;
    isActive.value = false;
    return;
  }

  sliderValue.value = numericValue;
  isActive.value = numericValue !== 0;
};

const formatValue = (value: number) => {
  if (props.dotVotingMeasurementType === constant.Form.DotVoting.MeasurementType.Money) {
    return formatMoney(value, props.languageEnabled);
  } else {
    return `${value} ${getLabel('form.dot-voting.points-abbreviation', props.languageEnabled)}`;
  }
};

// WATCH
watch(
  () => props.isSelected,
  (newValue) => {
    if (!newValue) {
      sliderValue.value = 0;
    }
  },
);

watch(sliderValue, (newValue) => {
  if (newValue === 0) {
    isActive.value = false;
    return;
  }
  isActive.value = true;
});

watch(
  () => props.selectedValue,
  (newValue) => {
    syncSliderValueWithSelection(newValue);
  },
  { immediate: true },
);
</script>
<template>
  <div
    class="dot-vote-choice__wrapper"
    :class="[
      {
        'dot-vote-choice__wrapper--selected':
          (props.isSelected && props.state === State.Question) ||
          (props.hasUserVotedOnChoice &&
            (props.state === State.ResultWithStats || props.state === State.ResultWithoutStats)) ||
          (props.isSelected && props.hasUserVotedOnChoice && props.state === State.NoContent),
      },
      {
        'dot-vote-choice__wrapper--notselected':
          !props.hasUserVotedOnChoice &&
          props.hasUserVoted &&
          (props.state === State.ResultWithStats || props.state === State.ResultWithoutStats),
      },
      { 'dot-vote-choice__wrapper--with-stats': props.state === State.ResultWithStats },
      { 'dot-vote-choice__wrapper--locked': props.state !== State.Question },
      { 'dot-vote-choice__wrapper--clickable': isFixedInteractiveButton },
    ]"
    :style="gradientFillStyle"
  >
    <component
      :is="interactiveTag"
      :type="isFixedInteractiveButton ? 'button' : undefined"
      :disabled="isFixedInteractiveButton && props.state === State.Question && props.isDisabled ? true : undefined"
      :role="interactiveRole"
      :aria-pressed="interactiveAriaPressed"
      :aria-disabled="interactiveAriaDisabled"
      :class="interactiveClasses"
      @click="isFixedInteractiveButton ? selectVote() : undefined"
    >
      <div class="dot-vote-choice">
        <div v-if="props.dot.image && props.dot.image.url && !isMobile" class="dot-vote-choice__image">
          <img :src="props.dot.image.url" :alt="getTranslation(props.dot, 'title', props.languageEnabled)" />
        </div>
        <div class="dot-vote-choice__content">
          <div class="dot-vote-choice__content-header">
            <div v-if="props.dot.image && props.dot.image.url && isMobile" class="image">
              <img :src="props.dot.image.url" :alt="getTranslation(props.dot, 'title', props.languageEnabled)" />
            </div>
            <div class="title">
              {{ getTranslation(props.dot, 'title', props.languageEnabled) }}
            </div>
          </div>
          <div v-if="!isMobile" class="dot-vote-choice__amount">
            {{ formatValue(choiceValue) }}
          </div>
          <div class="dot-vote-choice__content-description">
            <div v-if="isMobile" class="amount">
              {{ formatValue(choiceValue) }}
            </div>
            <div class="text">
              {{ getTranslation(props.dot, 'short_text', props.languageEnabled) }}
            </div>
            <div
              v-if="isMobile && !isHtmlEmpty(getTranslation(props.dot, 'long_text', props.languageEnabled))"
              style="min-height: 25px"
            >
              &nbsp;
            </div>
          </div>
        </div>
      </div>
    </component>
    <div
      v-if="props.state === State.ResultWithStats || props.state === State.ResultWithoutStats"
      class="dot-vote-choice__results"
    >
      <div class="dot-vote-choice__results-info">
        <div v-if="props.state === State.ResultWithStats" class="dot-vote-choice__results-percentage">
          <q-icon name="person" size="20px" class="dot-vote-choice__results-person" />
          <span>
            {{
              getLabel('form.dot-voting.results.users-voted', props.languageEnabled, {
                named: { percentage: votesPercentage },
              })
            }}
          </span>
        </div>
        <div
          v-if="props.state === State.ResultWithStats && variableStatsDisplay.length"
          class="dot-vote-choice__results-stats"
        >
          <q-icon name="bar_chart" size="20px" class="dot-vote-choice__results-stats-icon" />
          <div class="dot-vote-choice__results-stats-items">
            <span v-for="(item, index) in variableStatsDisplay" :key="item" class="dot-vote-choice__results-stat-item">
              {{ item }}
              <span v-if="index < variableStatsDisplay.length - 1" class="dot-vote-choice__results-stat-separator">
                |
              </span>
            </span>
          </div>
        </div>
      </div>
      <div v-if="props.hasUserVotedOnChoice" class="dot-vote-choice__results-icon">
        <q-icon name="check_circle" size="24px" color="primary" />
      </div>
    </div>
    <div
      v-if="props.dotVotingType === constant.Form.DotVoting.Type.Variable && props.state === State.Question"
      :class="['dot-vote-choice__slider', { 'dot-vote-choice__slider--disabled': isSliderDisabled }]"
    >
      <q-slider
        v-model="sliderValue"
        :min="0"
        :max="maxIndex"
        :step="1"
        label
        label-always
        markers
        :marker-labels="{
          0: formatValue(0),
          [maxIndex]: formatValue(maxValue),
        }"
        :label-value="sliderLabel"
        :label-color="labelColor"
        :label-text-color="labelTextColor"
        :inner-max="innerMax"
        track-size="5px"
        :disable="isSliderDisabled"
        :aria-label="getTranslation(props.dot, 'title', props.languageEnabled)"
        :aria-valuetext="sliderLabel"
        :aria-disabled="isSliderDisabled || undefined"
        @update:model-value="updateSlider"
        @click.stop
      />
    </div>
    <button
      v-if="!isHtmlEmpty(getTranslation(props.dot, 'long_text', props.languageEnabled))"
      type="button"
      class="dot-vote-choice__see-more"
      @click.stop="openSeeMoreDialog"
    >
      {{ getLabel('buttons.see-more', props.languageEnabled) }}
    </button>
    <dot-voting-choice-modal
      :dot="props.dot"
      :language-enabled="props.languageEnabled"
      ref="dotVotingChoiceDialogRef"
    />
  </div>
</template>
<style lang="scss">
.dot-vote-choice__wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background-color: $color-neutral-white;
  border: solid 1px $color-neutral-grey-300;
  border-radius: 8px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    width: var(--dot-vote-percentage, 0%);
    background: $color-primary-12;
    opacity: 0;
    transition:
      width 0.3s ease,
      opacity 0.3s ease;
    z-index: 0;
  }

  &--selected {
    border: solid 2px $color-primary;
    background-color: $color-primary-12;
  }

  &--disabled {
    opacity: 1;
    pointer-events: none;
    border: solid 1px $color-neutral-grey-400;
    cursor: default;
  }

  &--notselected {
    background-color: $color-neutral-grey-300;
  }

  &--locked {
    cursor: default;
  }

  &--with-stats {
    &::before {
      opacity: 1;
    }

    &.dot-vote-choice__wrapper--selected {
      background: $color-neutral-grey-300;
    }
  }

  &--clickable {
    cursor: pointer;
  }

  .dot-vote-choice__interactive {
    padding: $space-md;
    border: none;
    background: none;
    text-align: left;
    width: 100%;
    display: block;
    position: relative;
    z-index: 1;

    &--clickable {
      cursor: pointer;
    }

    &:disabled {
      opacity: 1;
      color: inherit;
      cursor: default;
    }
  }

  .dot-vote-choice {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: $space-md;
    color: $color-neutral-black-text;
    font-size: $body-text;
    height: 100%;
    position: relative;
    z-index: 1;

    &__image {
      display: flex;
      align-items: flex-start;
      width: inherit;
      height: 100%;
      align-self: stretch;

      img {
        width: 100px;
        height: 100px;
        border-radius: 4px;
      }
    }

    &__content {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        'header amount'
        'description amount';
      column-gap: $space-xl;
      row-gap: $space-sm;
      width: 100%;

      &-header {
        grid-area: header;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: $space-md;
        width: 100%;
        font-weight: bold;

        .title {
          line-height: normal;
          text-align: left;
          flex: 1 1 auto;
        }

        .image {
          display: flex;
          align-items: flex-start;
          width: 100px;
          height: 100%;
          align-self: stretch;

          img {
            width: 100px;
            height: 100px;
            border-radius: 4px;
          }
        }
      }

      &-description {
        grid-area: description;
        font-size: $small-text;
        text-align: left;
        padding-right: $space-md;

        .amount {
          display: flex;
          flex-direction: row;
          justify-content: center;
          padding: $space-sm 0;
          color: $color-primary-darken;
          font-weight: bold;
          font-size: $header;
        }

        .see-more {
          text-decoration: underline;
          cursor: pointer;
          text-align: left;
        }
      }
    }

    &__amount {
      grid-area: amount;
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
      white-space: nowrap;
      color: $color-primary-darken;
      font-weight: bold;
    }

    &__results {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      position: relative;
      z-index: 1;
      gap: $space-sm;
      flex-wrap: nowrap;

      &-info {
        display: inline-flex;
        flex: 1 1 auto;
        min-width: 0;
        flex-wrap: wrap;
        align-items: center;
        gap: $space-sm;
      }

      &-percentage {
        display: inline-flex;
        align-items: center;
        gap: $space-xs;
        font-size: $xsmall-text;
        line-height: 1.2;

        img {
          vertical-align: middle;
          margin-right: $space-xs;
        }
      }

      &-person {
        display: inline-flex;
        align-items: center;
      }

      &-stats {
        display: inline-flex;
        align-items: center;
        gap: $space-xxs;
        font-size: $xsmall-text;
        line-height: 1.1;
        color: $color-neutral-black-text;
      }

      &-stats-items {
        display: inline-flex;
        flex-wrap: wrap;
        align-items: center;
        column-gap: $space-xs;
        row-gap: $space-xxs;
      }

      &-stat-item {
        display: inline-flex;
        align-items: center;
        gap: $space-xxs;
        line-height: 1.2;
      }

      &-stat-separator {
        margin: 0 $space-xxs;
        color: $color-neutral-grey-400;
      }

      &-stats-icon {
        display: inline-flex;
      }

      &-icon {
        margin-left: $space-md;
        display: inline-flex;
        align-items: center;
        align-self: center;
      }
    }

    &__slider {
      margin-top: $space-xl;
      position: relative;
      z-index: 1;

      .q-slider__thumb {
        height: 30px !important;
        width: 30px !important;

        &-shape {
          stroke-width: 0.5;

          path {
            stroke: $color-primary-darken;
            fill: $color-neutral-white;
          }
        }
      }

      .q-slider {
        &__marker-labels {
          width: 40%;
          position: relative;
          overflow-wrap: break-word;

          &-container {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
          }

          &:nth-child(2) {
            left: unset !important;
            text-align: end;
          }

          &--h-ltr {
            transform: none !important;
          }
        }
      }

      &--disabled {
        cursor: not-allowed;

        .q-slider,
        .q-slider__thumb,
        .q-slider__track-container,
        .q-slider__track,
        .q-slider__focus-ring {
          cursor: not-allowed;
        }
      }
    }
  }

  .dot-vote-choice__see-more {
    position: absolute;
    right: $space-md;
    top: calc($space-md + 2.25rem);
    transform: none;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background-color: transparent;
    border: 1.5px solid $color-neutral-black-text;
    border-radius: 4px;
    padding: 2px 4px;
    color: $color-neutral-black-text;
    text-decoration: none;
    cursor: pointer;
    font-size: 12px;
  }
}

@media screen and (max-width: $breakpoint-lg) {
  .dot-vote-choice__wrapper {
    .dot-vote-choice {
      &__content {
        grid-template-columns: 1fr;
        grid-template-areas:
          'header'
          'description';

        &-header {
          gap: $space-md;

          .title {
            width: 100%;
            height: inherit;
          }
        }
      }
    }

    .dot-vote-choice__see-more {
      top: auto;
      bottom: 3rem;
      transform: none;
    }
  }
}

.dot-vote-choice__results {
  padding: 0 $space-md $space-md;
}

.dot-vote-choice__slider {
  padding: 0 $space-md $space-md;
}
</style>
